import { LitElement, html } from 'lit';
import { styles } from './css';
import { classMap } from 'lit/directives/class-map.js';
import { imagebossUrl } from '../../../../helpers/imageboss';
import {
  isFeatureEnabled,
  logErrorToSentry,
  useCartConfirmationModal,
} from '../../../../helpers/helpers';

import '../../../../lib/lit-3form-block/components/cta-text';

import {
  addToCart,
  checkProductAvailability,
  fetchCart,
} from '@Root/spree/api';

import {
  updateCartIndicator,
  changeProductImageInModal,
  changeProductNameInModal,
  changeProductSizeInfoInModal,
} from '@Root/spree/dom';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '../../../../lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '../../../../lib/lit-styles.js';

const getInit = { method: 'GET', headers: { Accept: 'application/json' } };
const productQuantityInputId = 'product-quantity';
const hushProductOrderingFeature = 'hushProductOrdering';
const invalidPriceString = 'N/A';

export class CustomizeChoicesBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      choicesData: { type: Array },
      mobileSwatchesPerPage: { type: Number },
      desktopSwatchesPerPage: { type: Number },
      hardwareFinishId: { type: Number },
      _termsAccepted: { type: Boolean },
      _alertTermsRequired: { type: Boolean },
      _placingOrder: { type: Boolean },
      _currentSelectionKey: { type: String },
      _expanded: { type: Boolean },
      _loadingData: { type: Boolean },
      _productQuantity: { type: Number },
      _selectionData: { type: Object },
      _selectedProductIsAvailable: { type: Boolean },
      _selectedProductSpreeData: { type: Object },
      _priceString: { type: String },
      _totalPriceString: { type: String },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));
    this._selectionData = {};
    this._termsAccepted = false;
    this._alertTermsRequired = false;
    this._placingOrder = false;
    this._selectedProductIsAvailable = false;
    this._selectedProductSpreeData = null;
    this._priceString = '';
    this._productQuantity = 1;
    this._totalPriceString = invalidPriceString;
    this.mobileSwatchesPerPage = 10;
    this.desktopSwatchesPerPage = 24;
  }

  async connectedCallback() {
    super.connectedCallback();
    this.addEventListener('scrollToVisualize', e => {
      this.changeBarrelVisibilty('open');
    });
    this.choicesData = this.formatChoicesData;
    this._expanded = true;
    this.setHardwareFinishId();
    this.setupSpreeData();
  }

  render() {
    return html`
      <div
        class=${classMap({
          'barrel-wrapper': true,
          expanded: this._expanded,
          'print-none': true,
        })}
      >
        <div
          id="visualization-barrel-header"
          class=${classMap({
            'barrel-header': true,
            hide: this._expanded,
          })}
          @click="${this.changeBarrelVisibilty}"
        >
          <span class="barrel-heavy-text">
            <span>${this.barrelTitle}</span>
          </span>
          <span class="barrel-header-icon print-none">
            <img src=${this.barrelHeaderIcon} />
          </span>
        </div>
        ${this.choicesData.map(choice => this.choiceHTML(choice))}
        ${this.ctaHtml}
      </div>
    `;
  }

  choiceHTML(choice) {
    return html`
      <div class="pattern-options-wrapper">
        ${!!choice.title
          ? html`
              <div class="pattern-options-header">
                <h3 class="barrel-heavy-text">
                  ${choice.title}
                </h3>
              </div>
            `
          : html``}
        ${choice.option_groups.map(optionGroup =>
          this.optionGroupHTML(optionGroup)
        )}
      </div>
    `;
  }

  optionGroupHTML(optionGroup) {
    return html`
      <div class="option-group-wrapper">
        ${!!optionGroup.title
          ? html`
              <h3>${optionGroup.title}</h3>
            `
          : html``}
        ${optionGroup.totalPages > 1
          ? html`
              <div class="pattern-filters print-none">
                <div class="input-wrapper search">
                  <input
                    type="text"
                    placeholder="Search by Name"
                    class="search-input"
                    id="${`search-input-${optionGroup.optionGroupIndex}`}"
                    @keyup=${e => {
                      e['optionGroup'] = optionGroup;
                      this.updateSearchSelection(e);
                    }}
                  />
                  <img class="search-icon" src="/static/icons/SVG/search.svg" />
                </div>
              </div>
            `
          : html``}
        <div class="swatches">
          ${optionGroup.options.map(option => this.optionsSwatchHTML(option))}
        </div>
        ${optionGroup.showPagination
          ? html`
              <div class="swatch-controls">
                <p class="counter description">
                  ${this.counterString(optionGroup)}
                </p>
                <span class="swatch-page-controls">
                  <span
                    @click="${() =>
                      this.changeSwatchPage(optionGroup, 'previous')}"
                    class="left-arrow"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                  <span
                    @click="${() => this.changeSwatchPage(optionGroup, 'next')}"
                    class="right-arrow"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                </span>
              </div>
            `
          : html``}
      </div>
    `;
  }

  optionsSwatchHTML(option) {
    return option.show
      ? html`
          <div
            class="swatch-wrapper"
            @click="${() => this.updateChoiceSelection(option)}"
          >
            <span
              class=${classMap({
                'image-swatch': !option.color_hex_code,
                'color-swatch': !!option.color_hex_code,
                'selected-swatch': option.selected,
                'not-selected-swatch': !option.selected,
              })}
            >
              ${!!option.color_hex_code
                ? html`
                    <span
                      style="background-color: #${option.color_hex_code}"
                    ></span>
                  `
                : html`
                    <img
                      src=${imagebossUrl(option.image?.src, {
                        height: 250,
                      })}
                      alt="${option.label}"
                    />
                  `}
            </span>
            <p class="swatch-name">${option.label}</p>
          </div>
        `
      : '';
  }

  get selectsProduct() {
    return (
      this.hushProductOrderingEnabled && this.content.selects_product === '1'
    );
  }

  get hushProductOrderingEnabled() {
    return isFeatureEnabled(hushProductOrderingFeature);
  }

  get ctaHtml() {
    return this.selectsProduct
      ? this.addToCartCtaHtml
      : html`
          <div class="cta-text-wrapper">
            <cta-button
              cta-data=${this.requestAQuoteCtaData}
              cta-type="primary"
              @click="${this.requestAQuote}"
            ></cta-button>
          </div>
        `;
  }

  get addToCartCtaHtml() {
    return html`
      <div id="add-choice-to-cart">
        <div class="input-row-wrapper">
          <span class="input-row-header">
            <p class="barrel-heavy-text">Panel Size</p>
          </span>
          <span class="input-row-value">
            <p>${this.panelSizeText}</p>
          </span>
        </div>
        <div class="input-row-wrapper">
          <span class="input-row-header">
            <label for="${productQuantityInputId}" class="barrel-heavy-text"
              >Qty</label
            >
          </span>
          <span class="input-row-value">
            <input
              type="number"
              .value="${this._productQuantity}"
              min="1"
              step="1"
              @change="${this.productQuantityChanged}"
            />
          </span>
        </div>

        <div class="input-row-wrapper no-border">
          <span class="input-row-header">
            <p class="barrel-heavy-text total-price-header">
              Total (USD)
            </p>
          </span>
          <span class="input-row-value">
            <p class="total-price-text">${this._totalPriceString}</p>
            ${this.shippingIncludedHtml}
          </span>
        </div>
        <div class="terms wrapper">
          <div class="terms-checkbox">
            <input
              type="checkbox"
              name="terms"
              id="terms"
              @change="${this.termsClicked}"
              .checked="${this._termsAccepted}"
            />
            <label
              class=${classMap({
                terms: true,
                'terms-label': !this._alertTermsRequired,
                'terms-label-alert': this._alertTermsRequired,
              })}
              for="terms"
            >
              I agree to the
              <a href="/terms" target="_blank">Terms and Conditions</a>. All
              sales are final.
            </label>
          </div>
          <p class="barrel-text questions">
            If you have any questions about your order please contact your
            Materials Consultant or
            <u @click="${this.openChat}">
              chat with a Customer Service Rep.
            </u>
          </p>
        </div>
        <div class="ctas-wrapper">
          <cta-button
            cta-data=${this.addToCartCTAData}
            cta-type="primary"
            @click="${this.addSelectedProductToCart}"
            ?disabled="${this.addToCartDisabled}"
          ></cta-button>
          <cta-button
            .ctaData=${this.secondaryCtaData}
            cta-type="secondary"
          ></cta-button>
        </div>
        <div>
          <p>
            Have terms with us? Please contact us at customer.support@3-form.com
          </p>
        </div>
      </div>
    `;
  }

  productQuantityChanged(event) {
    if (!event || isNaN(event.target.value)) return;

    const newValue = event.target.value < 1 ? 1 : event.target.value;
    this._productQuantity = Math.ceil(newValue);
    this._totalPriceString = this.totalPrice;
    this.requestUpdate();
  }

  get barrelTitle() {
    return this.content.title.toUpperCase();
  }

  get barrelHeaderIcon() {
    return this._expanded
      ? '/static/icons/SVG/minimize-dash.svg'
      : '/static/icons/SVG/expand-plus.svg';
  }

  get formatChoicesData() {
    let formattedChoicesData = [];
    this.content.choices.forEach((choice, choiceIndex) => {
      if (choice.option_groups) {
        // add default values to optionGroups
        let formatteOptionGroups = [];
        choice.option_groups.forEach((optionGroup, optionGroupIndex) => {
          if (optionGroup.options) {
            optionGroup['optionGroupIndex'] = optionGroupIndex;
            optionGroup['currentPage'] = 1;
            optionGroup['totalPages'] = Math.ceil(
              optionGroup.options.length / this.swatchesPerPage
            );
            optionGroup['searchString'] = '';
            optionGroup['showPagination'] = optionGroup.totalPages > 1;

            // add default values to options
            let formattedOptions = [];
            optionGroup.options.forEach((option, optionIndex) => {
              option['choiceIndex'] = choiceIndex;
              option['optionGroupIndex'] = optionGroupIndex;
              option['selected'] = false;
              // page needs to be at least 1 so optionIndex can't be 0 for this
              // math to work
              option['page'] = Math.ceil(
                (optionIndex + 1) / this.swatchesPerPage
              );
              option['show'] = option.page === optionGroup.currentPage;
              formattedOptions.push(option);
            });
            optionGroup.options = formattedOptions;
          }
          formatteOptionGroups.push(optionGroup);
        });
        formattedChoicesData.push(choice);
      }
    });
    return formattedChoicesData;
  }

  setHardwareFinishId() {
    fetch(this.content.threekit_hardware_finishes_path, getInit)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        this.hardwareFinishId = json?.threekit_hardware_finishes[0]?.id || null;
        this.makeSelectionsFromQueryParams();
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  makeSelectionsFromQueryParams() {
    // Load choices from url query params if present
    // query params look like this:
    //   ?populate_choices=true&{parentBlockId}_{choiceId}={optionId}
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams) {
      const populateChoices = !!JSON.parse(
        searchParams.get('populate_choices')
      );
      if (populateChoices) {
        const parentBlockId = this.content.parentBlockId;
        for (const [key, value] of searchParams.entries()) {
          if (key.includes(`${parentBlockId}_`)) {
            let choiceId = key.replace(`${parentBlockId}_`, '');
            this.choicesData.forEach(choice => {
              if (choice.id == choiceId) {
                choice.option_groups.forEach(optionGroup => {
                  optionGroup?.options.forEach(option => {
                    if (option.id == value) option.selected = true;
                  });
                });
              }
            });
          }
        }
        this.updateSelections();
      }
    }
  }

  get swatchesPerPage() {
    return window.useMobileLayout
      ? this.mobileSwatchesPerPage
      : this.desktopSwatchesPerPage;
  }

  get numberOfChoices() {
    return this.choicesData.length;
  }

  changeBarrelVisibilty(override = '') {
    switch (override) {
      case 'open':
        this._expanded = true;
        break;
      case 'close':
        this._expanded = false;
        break;
      default:
        this._expanded = !this._expanded;
        break;
    }
  }

  requestAQuote() {
    const requestMoreInfoFromBarrel = new CustomEvent(
      'requestMoreInfoFromBarrel',
      {
        bubbles: true,
        composed: true,
        detail: {
          cta: 'Request a Quote',
          url: '/request-quote',
        },
      }
    );
    this.dispatchEvent(requestMoreInfoFromBarrel);
  }

  get requestAQuoteCtaData() {
    return JSON.stringify({
      text: 'Request a Quote',
      url: '/request-quote',
      fullWidth: true,
    });
  }

  get addToCartCTAData() {
    return JSON.stringify({
      text: 'Add to Cart',
      fullWidth: true,
    });
  }

  get secondaryCtaData() {
    const externalId = this.selectedSpreeProduct.id;
    const ctaURL = externalId
      ? `/estimates/${externalId}?qty=${this._productQuantity}`
      : null;

    return {
      text: 'Print Estimate',
      url: ctaURL,
      fullWidth: true,
      target: '_blank',
    };
  }

  updateChoiceSelection(selectedOption) {
    if (selectedOption.selected) return;

    // removes previous selection in that group and updates to new selection
    this.choicesData[selectedOption.choiceIndex].option_groups.forEach(
      group => {
        group.options.forEach(option => {
          option.selected = selectedOption.id === option.id;
        });
      }
    );

    // UI starts with no selections made, when one choice has been made all
    // other choices have their first item selected so a rendered image is shown
    if (!this.allChoicesMade) {
      this.choicesData.forEach(choice => {
        choice.option_groups.forEach(group => {
          if (group.options.some(option => option.selected)) return;
          group.options[0].selected = true;
        });
      });
    }

    this.updateSelections();
  }

  updateSelections() {
    // only fire if all choices have been made
    if (this.allChoicesMade) {
      const allSelections = this.allUserSelections;
      const filenamePartsString = allSelections
        .map(selection => {
          if (selection.is_hardware_option === '1') {
            this.hardwareFinishId = selection.value;
            return null;
          } else {
            return selection.value;
          }
        })
        .filter(Boolean)
        .join('&');
      const searchParams = new URLSearchParams({
        threekit_hardware_finish_id: this.hardwareFinishId,
        threekit_stage_id: this.content.threekit_stage_id,
        render_interlayer_filename_part: filenamePartsString,
      });
      const layersUrl = `/threekit_layers?${searchParams}`;
      this._currentSelectionKey = layersUrl;

      if (this.currentSelectionData) {
        return this.dispatchSelectionEvent(this.loadedThreekitImages);
      }

      const init = {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      this._loadingData = true;
      this.dispatchFetchingCarouselImage();

      fetch(layersUrl, init)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(json => {
          if (!!json) {
            json.threekit_layers = this.addChoiceDataToImages(
              json.threekit_layers,
              allSelections
            );
            this._selectionData[layersUrl] = json;
          }
          this.dispatchSelectionEvent(this.loadedThreekitImages);
          this.checkSelectedProductIsAvailable();
          this._loadingData = false;
        })
        .catch(error => {
          this._loadingData = false;
          logErrorToSentry(error);
        });
    }

    this.requestUpdate();
  }

  dispatchFetchingCarouselImage() {
    const fetchingNewCarouselImage = new CustomEvent(
      'fetchingNewCarouselImage',
      {
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(fetchingNewCarouselImage);
  }

  dispatchSelectionEvent(selectionImages) {
    const newCarouselImagesFromBarrel = new CustomEvent(
      'newCarouselImagesFromBarrel',
      {
        detail: {
          selectionImages,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(newCarouselImagesFromBarrel);
    this.requestUpdate();
  }

  addChoiceDataToImages(images, selections) {
    images.forEach(image => {
      let humanizedSelectionData = [];
      selections.forEach(selection => {
        const selectionLabel = selection.selectionLabel || selection.label;
        if (selectionLabel) {
          humanizedSelectionData.push({
            selectionLabel: selectionLabel,
            value: selection.label,
          });
        }
      });
      const numberOfSelections = humanizedSelectionData.length;
      if (numberOfSelections) {
        humanizedSelectionData.forEach((entry, index) => {
          image.thumbnail_description =
            `${image.thumbnail_description || ''}` +
            `${entry.selectionLabel} | ${entry.value}`;
          if (index + 1 !== numberOfSelections) {
            image.thumbnail_description = image.thumbnail_description + '\n';
          }
        });
      }
    });
    return images;
  }

  get allChoicesMade() {
    let foundChoices = [];

    this.choicesData.forEach(choice => {
      let choiceFound = false;
      choice.option_groups.forEach(group => {
        if (group.options.some(option => option.selected)) {
          choiceFound = true;
        }
      });
      foundChoices.push(choiceFound);
    });

    // only return true if every entry in foundChoices is truthy
    return foundChoices.every(choiceIsMade => choiceIsMade);
  }

  get choicesCount() {
    return this.choicesData.length;
  }

  get allUserSelections() {
    let allValues = [];

    this.choicesData.forEach((choice, choiceIndex) => {
      // this order matters for the threekit query params
      const concatenationOrder =
        typeof choice.concatenation_order !== 'undefined' &&
        choice.concatenation_order !== null
          ? choice.concatenation_order
          : choiceIndex;
      choice.option_groups.forEach(group => {
        allValues.splice(
          concatenationOrder,
          0,
          group.options.find(option => option.selected)
        );
      });
    });

    allValues.forEach(value => {
      value['selectionLabel'] = this.singularizedLabels(value);
    });
    return allValues;
  }

  get currentSelectionData() {
    return this._selectionData[this._currentSelectionKey];
  }

  get loadedThreekitImages() {
    return this.currentSelectionData?.threekit_layers;
  }

  get selectedSpreeProduct() {
    return this.currentSelectionData?.spree_product || {};
  }

  get panelSizeText() {
    return this.selectedSpreeProduct.panel_size_text || 'N/A';
  }

  singularizedLabels(choice) {
    let label =
      this.choicesData[choice.choiceIndex].label ||
      this.choicesData[choice.choiceIndex]?.option_groups[0].title;
    const conversions = [
      ['Colors', 'Color'],
      ['Patterns', 'Pattern'],
      ['Hardware Finishes', 'Hardware Finish'],
      ['Front Color', 'Front Color'],
      ['Back Color', 'Back Color'],
    ];
    conversions.forEach(conversion => {
      if (conversion[0] === label) label = conversion[1];
    });
    return label;
  }

  changeSwatchPage(optionGroup, direction) {
    if (direction === 'previous') {
      optionGroup.currentPage === 1
        ? (optionGroup.currentPage = optionGroup.totalPages)
        : optionGroup.currentPage--;
    } else if (direction === 'next') {
      optionGroup.currentPage === optionGroup.totalPages
        ? (optionGroup.currentPage = 1)
        : optionGroup.currentPage++;
    }
    optionGroup.options.forEach(
      option => (option.show = option.page === optionGroup.currentPage)
    );
    this.requestUpdate();
  }

  counterString(optionGroup) {
    return `Page ${optionGroup.currentPage} of ${optionGroup.totalPages}`;
  }

  updateSearchSelection(e) {
    let inputValue = e.target.value.toLowerCase();
    const optionGroup = e.optionGroup;
    if (inputValue.length <= 1) inputValue = '';
    if (inputValue !== optionGroup.searchString) {
      optionGroup.searchString = inputValue;
      let shownResults = 0;
      optionGroup.options.forEach((option, index) => {
        option.show = inputValue
          ? option.label.toLowerCase().includes(inputValue) &&
            shownResults < this.swatchesPerPage
          : option.page === optionGroup.currentPage;
        if (option.show) shownResults++;
      });
      optionGroup.showPagination = inputValue
        ? shownResults > this.swatchesPerPage
        : optionGroup.options.length > this.swatchesPerPage;
    }
    this.requestUpdate();
  }

  get addToCartDisabled() {
    return (
      this._loadingData ||
      !this.selectedSpreeProduct ||
      !this._termsAccepted ||
      !this._selectedProductIsAvailable
    );
  }

  async checkSelectedProductIsAvailable() {
    this._selectedProductIsAvailable = false;
    if (!this.selectedSpreeProduct?.id) return false;

    const productAvailabilityResponse = await checkProductAvailability(
      this.selectedSpreeProduct.id
    );

    if (productAvailabilityResponse.isSuccess()) {
      this._selectedProductIsAvailable = true;
      this.setupProductData(this.selectedSpreeProduct.id);
    }
  }

  async setupProductData(spreeProductId) {
    if (!window.SpreeAPI || !spreeProductId) return;
    const spreeHost = window.SpreeAPI.host;

    const url = `${spreeHost}/api/v2/storefront/products/${spreeProductId}?include=product_properties`;
    const options = {
      method: 'GET',
      headers: { Accept: 'application/vnd.api+json' },
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      this._selectedProductSpreeData = data?.data?.attributes || null;
      this._priceString = this._selectedProductSpreeData?.price;
      this._totalPriceString = this.totalPrice;
      // TODO: add anayltics handling for when we go live
      // if (this._fullSpreeData && this.expanded) {
      //   this.sendAnaylticsEvent('view_item');
      // }
      this.requestUpdate();
    } catch (error) {
      this._fullSpreeData = {};
      logErrorToSentry(error);
    }
  }

  async setupSpreeData() {
    if (!this.cart) {
      this.cart = await fetchCart();
    }
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);
  }

  async addSelectedProductToCart() {
    if (!this._termsAccepted || this.orderBtnDisabled) {
      this._alertTermsRequired = true;
      this.requestUpdate();
      return;
    }

    useCartConfirmationModal();
    this._placingOrder = true;

    this.cart = await addToCart(
      this.selectedSpreeProduct.variant_id,
      this._productQuantity
    );
    let header = this._selectedProductSpreeData.name;

    if (this.cart) {
      // TODO add analytics event
      // this.sendAnaylticsEvent('add_to_cart');
      updateCartIndicator(this.cart.item_count);
      changeProductImageInModal(this.spreeImgUrl);
      changeProductNameInModal(header);
      changeProductSizeInfoInModal(this.productSize, '', true);
    }

    this._placingOrder = false;
  }

  get productSize() {
    return this.selectedSpreeProduct?.panel_size_text || '48" x 96" Panel';
  }

  get productQuantityInput() {
    return this.renderRoot.querySelector(`#${productQuantityInputId}`);
  }

  termsClicked(event) {
    this._termsAccepted = event.target.checked;
    if (this._termsAccepted && this._alertTermsRequired) {
      this._alertTermsRequired = false;
    }
  }

  get spreeImgUrl() {
    if (this.loadedThreekitImages && this.loadedThreekitImages[0]) {
      const baseUrl = this.loadedThreekitImages[0].src;
      return imagebossUrl(baseUrl, {
        width: 258,
      });
    } else {
      return '';
    }
  }

  get orderBtnDisabled() {
    return !this._termsAccepted || this.hasInvalidPrice || this._placingOrder;
  }

  get hasInvalidPrice() {
    return this._totalPriceString === invalidPriceString;
  }

  get totalPrice() {
    const price = this._priceString || null,
      quantity = this._productQuantity || null;
    if (!price || !quantity) return invalidPriceString;

    const priceNumber = parseFloat(price.replace(/[$,]/g, '')) * quantity;
    if (!priceNumber) return invalidPriceString;

    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    return `$${priceNumber.toLocaleString('en-US', options)} (USD)`;
  }

  get shippingIncludedHtml() {
    if (this.hasInvalidPrice) return html``;

    return html`
      <p>Shipping Included</p>
    `;
  }

  openChat() {
    if (typeof brandembassy === 'function') {
      brandembassy('openChatWindow');
    }
  }
}
customElements.define('customize-choices-barrel', CustomizeChoicesBarrel);
